import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Shield, Building2, Coins, Settings2, AlertTriangle, Zap, CloudSun, Receipt, Server } from "lucide-react";
import EnterprisePanel from "@/pages/EnterprisePanel";
import { LockedAccountsPanel } from "@/components/LockedAccountsPanel";
import { PendingAssignmentsPanel } from "@/components/PendingAssignmentsPanel";
import { FailedLoginsPanel } from "@/components/FailedLoginsPanel";
import { UsersPanel } from "@/components/UsersPanel";
import { api, formatApiErrorDetail } from "@/lib/api";
import { formatDate } from "@/lib/dateFormat";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function Kpi({ label, value, unit, tone = "default", testId }) {
  const toneCls =
    tone === "warn"
      ? "border-destructive/50 text-destructive"
      : tone === "primary"
        ? "border-primary/60 text-primary"
        : "border-border";
  return (
    <Card className={toneCls}>
      <CardContent className="p-5">
        <div className="label-caps">{label}</div>
        <div className="mt-1 flex items-baseline gap-2" data-testid={testId}>
          <span className="font-mono-jb text-3xl font-semibold">{value}</span>
          {unit && <span className="text-sm text-muted-foreground">{unit}</span>}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [munis, setMunis] = useState([]);
  const [credits, setCredits] = useState(null);
  const [settings, setSettings] = useState(null);
  const [creatingMuni, setCreatingMuni] = useState(false);
  const [budgetEditing, setBudgetEditing] = useState(null); // muni row

  const reload = async () => {
    const [o, m, c, s] = await Promise.all([
      api.get("/admin/overview"),
      api.get("/admin/municipalities"),
      api.get("/admin/credits/summary"),
      api.get("/admin/settings"),
    ]);
    setOverview(o.data);
    setMunis(m.data);
    setCredits(c.data);
    setSettings(s.data);
  };

  useEffect(() => {
    reload().catch(() => {});
  }, []);

  if (!user?.is_super_admin) {
    return (
      <div className="mx-auto max-w-md py-10 text-center">
        <Shield className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
        <h1 className="text-2xl font-semibold">{t("super_admin_only")}</h1>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="label-caps text-primary">
            <Shield className="mr-1 inline h-3.5 w-3.5" /> {t("super_admin_kicker")}
          </div>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
            {t("master_dashboard")}
          </h1>
        </div>
        {credits?.alert_triggered && (
          <div
            className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            data-testid="credit-alert-banner"
          >
            <AlertTriangle className="h-4 w-4" />
            {t("credit_alert_triggered")}
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="admin-tab-overview">{t("overview")}</TabsTrigger>
          <TabsTrigger value="munis" data-testid="admin-tab-munis">{t("municipalities_tab")}</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Nutzer</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="admin-tab-bookings">{t("all_bookings")}</TabsTrigger>
          <TabsTrigger value="credits" data-testid="admin-tab-credits">{t("credits")}</TabsTrigger>
          <TabsTrigger value="enterprise" data-testid="admin-tab-enterprise">
            <Server className="mr-1 h-3.5 w-3.5" />{t("enterprise_tab")}
          </TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">{t("settings_short")}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          {overview && (
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <Kpi label={t("municipalities_tab")} value={overview.municipalities} tone="primary" testId="kpi-munis" />
              <Kpi label={t("users")} value={overview.users} testId="kpi-users" />
              <Kpi label={t("utility")} value={overview.utilities} testId="kpi-utilities" />
              <Kpi label={t("pool_owner")} value={overview.pool_owners} testId="kpi-pool-owners" />
              <Kpi label={t("bookings")} value={overview.bookings_total} testId="kpi-bookings" />
              <Kpi label={t("pending")} value={overview.bookings_pending} tone={overview.bookings_pending ? "warn" : "default"} testId="kpi-pending" />
              <Kpi label={t("large_consumer")} value={overview.large_pending} unit={t("pending").toLowerCase()} testId="kpi-lc-pending" />
            </div>
          )}
        </TabsContent>

        <TabsContent value="munis" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setCreatingMuni(true)} data-testid="admin-new-muni-btn" className="gap-2">
              <Building2 className="h-4 w-4" /> {t("new_municipality")}
            </Button>
          </div>
          <div className="grid gap-4">
            {munis.map((m) => (
              <MunicipalityRow
                key={m.id}
                m={m}
                onEditBudget={() => setBudgetEditing(m)}
                onChanged={reload}
                t={t}
              />
            ))}
            {munis.length === 0 && (
              <Card className="border-border">
                <CardContent className="p-6 text-sm text-muted-foreground">
                  {t("no_data")}
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="bookings" className="space-y-4">
          <AllBookingsPanel onChange={reload} t={t} />
        </TabsContent>

        <TabsContent value="credits" className="space-y-4">
          {credits && (
            <>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                <Kpi label={t("today")} value={credits.today_billed} unit="Credits" tone="primary" testId="kpi-today-billed" />
                <Kpi label={t("this_month")} value={credits.month_billed} unit={`/ ${credits.alert_threshold}`} tone={credits.alert_triggered ? "warn" : "default"} testId="kpi-month-billed" />
                <Kpi label={t("internal_cost")} value={credits.month_internal} unit="Emergent" testId="kpi-month-internal" />
                <Kpi label={t("margin")} value={`${credits.month_billed - credits.month_internal}`} unit="+50%" tone="primary" testId="kpi-margin" />
              </div>
              <Card className="border-border">
                <CardContent className="p-5">
                  <h3 className="mb-3 text-sm font-semibold">{t("by_feature_month")}</h3>
                  {credits.by_feature.length === 0 ? (
                    <div className="text-sm text-muted-foreground">{t("no_data")}</div>
                  ) : (
                    <ul className="space-y-2">
                      {credits.by_feature.map((f) => (
                        <li
                          key={f.feature}
                          className="flex items-center justify-between text-sm"
                          data-testid={`feature-row-${f.feature}`}
                        >
                          <span className="flex items-center gap-2">
                            <Zap className="h-3.5 w-3.5 text-primary" />
                            {f.feature}
                          </span>
                          <span className="font-mono-jb">{f.billed} Cr · int. {f.internal}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
              <Card className="border-border">
                <CardContent className="p-5">
                  <h3 className="mb-3 text-sm font-semibold">{t("by_municipality_month")}</h3>
                  {credits.by_municipality.length === 0 ? (
                    <div className="text-sm text-muted-foreground">{t("no_data")}</div>
                  ) : (
                    <ul className="space-y-2">
                      {credits.by_municipality.map((m) => (
                        <li
                          key={m.municipality_id}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{m.municipality_name}</span>
                          <span className="font-mono-jb">{m.billed} Cr</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="users" className="space-y-4">
          <UsersPanel onChanged={reload} />
        </TabsContent>

        <TabsContent value="enterprise" className="space-y-4">
          <EnterprisePanel munis={munis} settings={settings} onReload={reload} />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          {settings && <PlatformSettingsPanel initial={settings} onSaved={reload} t={t} />}
          <PendingAssignmentsPanel />
          <LockedAccountsPanel />
          <FailedLoginsPanel />
        </TabsContent>
      </Tabs>

      {creatingMuni && (
        <NewMuniDialog
          onClose={() => setCreatingMuni(false)}
          defaultBudget={settings?.default_monthly_budget ?? 1000}
          onSaved={async () => {
            setCreatingMuni(false);
            await reload();
          }}
          t={t}
        />
      )}
      {budgetEditing && (
        <BudgetDialog
          muni={budgetEditing}
          onClose={() => setBudgetEditing(null)}
          onSaved={async () => {
            setBudgetEditing(null);
            await reload();
          }}
          t={t}
        />
      )}
    </div>
  );
}

function MunicipalityRow({ m, onEditBudget, onChanged, t }) {
  const [flags, setFlags] = useState({
    live_weather: !!m.feature_flags?.live_weather,
    in_app_credit_billing: !!m.feature_flags?.in_app_credit_billing,
  });
  const [billingMode, setBillingMode] = useState(m.billing_mode || "credit");
  const [tierPrice, setTierPrice] = useState(m.tier_fixed_price_eur || 0);
  const [savingPrice, setSavingPrice] = useState(false);

  const patch = async (body, successMsg = t("flags_saved")) => {
    try {
      await api.patch(`/admin/municipalities/${m.id}/flags`, body);
      toast.success(successMsg);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const toggleFlag = (key) => async (checked) => {
    setFlags((f) => ({ ...f, [key]: checked }));
    await patch({ [key]: checked });
  };

  const changeBillingMode = async (value) => {
    setBillingMode(value);
    await patch({ billing_mode: value });
  };

  const saveTierPrice = async () => {
    setSavingPrice(true);
    try {
      await patch({ tier_fixed_price_eur: parseFloat(tierPrice) || 0 });
    } finally {
      setSavingPrice(false);
    }
  };

  const pct = m.monthly_credit_budget > 0
    ? Math.min(100, Math.round((m.credits_used_month / m.monthly_credit_budget) * 100))
    : 0;
  const heat = pct >= 90 ? "text-destructive" : pct >= 60 ? "text-yellow-600" : "text-primary";

  return (
    <Card className="border-border" data-testid={`admin-muni-row-${m.id}`}>
      <CardContent className="p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold" data-testid={`muni-name-${m.id}`}>{m.name}</h3>
            <div className="mt-0.5 text-xs text-muted-foreground">
              {m.pool_owner_count} {t("pool_owner")} · {m.bookings_count} {t("bookings")} · {m.daily_quota_m3} m³/{t("date").toLowerCase()}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onEditBudget}
            data-testid={`admin-edit-budget-${m.id}`}
          >
            {t("set_budget")}
          </Button>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {/* Modules */}
          <div className="rounded-md border border-border p-3">
            <div className="label-caps mb-2 flex items-center gap-1">
              <CloudSun className="h-3.5 w-3.5" />
              {t("module_flags")}
            </div>
            <div className="space-y-2 text-sm">
              <label className="flex items-center justify-between gap-2" htmlFor={`lw-${m.id}`}>
                <span>{t("module_live_weather")}</span>
                <Switch
                  id={`lw-${m.id}`}
                  checked={flags.live_weather}
                  onCheckedChange={toggleFlag("live_weather")}
                  data-testid={`toggle-live-weather-${m.id}`}
                />
              </label>
              <label className="flex items-center justify-between gap-2" htmlFor={`cb-${m.id}`}>
                <span>{t("module_credit_billing")}</span>
                <Switch
                  id={`cb-${m.id}`}
                  checked={flags.in_app_credit_billing}
                  onCheckedChange={toggleFlag("in_app_credit_billing")}
                  data-testid={`toggle-credit-billing-${m.id}`}
                />
              </label>
            </div>
          </div>

          {/* Billing mode */}
          <div className="rounded-md border border-border p-3">
            <div className="label-caps mb-2 flex items-center gap-1">
              <Receipt className="h-3.5 w-3.5" />
              {t("billing_mode")}
            </div>
            <Select value={billingMode} onValueChange={changeBillingMode}>
              <SelectTrigger data-testid={`billing-mode-${m.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">{t("billing_credit")}</SelectItem>
                <SelectItem value="tiered">{t("billing_tiered")}</SelectItem>
                <SelectItem value="enterprise">{t("billing_enterprise")}</SelectItem>
              </SelectContent>
            </Select>
            {billingMode === "tiered" && (
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={tierPrice}
                  onChange={(e) => setTierPrice(e.target.value)}
                  placeholder={t("tier_fixed_price")}
                  data-testid={`tier-price-${m.id}`}
                />
                <Button size="sm" onClick={saveTierPrice} disabled={savingPrice}>
                  {savingPrice ? "…" : t("save")}
                </Button>
              </div>
            )}
          </div>

          {/* Cost */}
          <div className="rounded-md border border-border p-3">
            <div className="label-caps mb-2 flex items-center gap-1">
              <Coins className="h-3.5 w-3.5" />
              {t("cost_month_eur")}
            </div>
            {billingMode === "tiered" ? (
              <div className="text-sm">
                <div className="font-mono-jb text-lg font-semibold" data-testid={`fixed-price-${m.id}`}>
                  {(m.tier_fixed_price_eur || 0).toFixed(2)} €
                </div>
                <div className="text-xs text-muted-foreground">{t("billing_tiered")}</div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {t("cost_internal_short")}: <span className="font-mono-jb">{m.internal_cost_eur?.toFixed?.(2) ?? "0.00"} €</span>
                </div>
              </div>
            ) : (
              <div className="text-sm">
                <div className={`font-mono-jb text-lg font-semibold ${heat}`} data-testid={`billed-cost-${m.id}`}>
                  {(m.billed_cost_eur ?? 0).toFixed(2)} €
                </div>
                <div className="text-xs text-muted-foreground">
                  {m.credits_used_month} Cr · {pct}% {t("credits_used").toLowerCase()}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {t("cost_internal_short")}: <span className="font-mono-jb">{m.internal_cost_eur?.toFixed?.(2) ?? "0.00"} €</span>
                  {" "}({m.internal_credits_month || 0} Cr)
                </div>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function AllBookingsPanel({ onChange, t }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get("/admin/bookings").then((r) => setRows(r.data)).catch(() => {});
  }, []);
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}/status`, { status });
      toast.success("✓");
      const r = await api.get("/admin/bookings");
      setRows(r.data);
      onChange?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };
  return (
    <Card className="border-border">
      <CardContent className="p-0">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left label-caps">
              <th className="px-4 py-3">{t("date")}</th>
              <th className="px-4 py-3">{t("municipality")}</th>
              <th className="px-4 py-3">{t("name")}</th>
              <th className="px-4 py-3">{t("volume_m3")}</th>
              <th className="px-4 py-3">{t("status")}</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((b) => (
              <tr key={b.id} className="border-b border-border/60" data-testid={`admin-booking-row-${b.id}`}>
                <td className="px-4 py-3 font-mono-jb" title={b.date}>{formatDate(b.date)}</td>
                <td className="px-4 py-3">{b.municipality_name}</td>
                <td className="px-4 py-3">{b.user_name}</td>
                <td className="px-4 py-3 font-mono-jb">{b.volume_m3}</td>
                <td className="px-4 py-3">{t(b.status)}</td>
                <td className="px-4 py-3">
                  {b.status === "pending" && (
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => setStatus(b.id, "approved")} data-testid={`admin-approve-${b.id}`}>
                        {t("approve")}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setStatus(b.id, "rejected")} data-testid={`admin-reject-${b.id}`}>
                        {t("reject")}
                      </Button>
                    </div>
                  )}
                  {b.status === "approved" && (
                    <Button size="sm" variant="secondary" onClick={() => setStatus(b.id, "completed")}>
                      {t("mark_as_done")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

function NewMuniDialog({ onClose, onSaved, defaultBudget, t }) {
  const [name, setName] = useState("");
  const [quota, setQuota] = useState(100);
  const [budget, setBudget] = useState(defaultBudget);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/municipalities", {
        name,
        daily_quota_m3: parseFloat(quota),
        monthly_credit_budget: parseInt(budget, 10),
      });
      toast.success(t("municipality_created"));
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-new-muni-dialog">
        <DialogHeader>
          <DialogTitle>{t("new_municipality")}</DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div>
            <Label>{t("name")}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-muni-name-input" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("quota_m3_day")}</Label>
              <Input type="number" min="0" step="1" value={quota} onChange={(e) => setQuota(e.target.value)} />
            </div>
            <div>
              <Label>{t("credit_budget")}</Label>
              <Input type="number" min="0" step="10" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="admin-muni-budget-input" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t("cancel")}</Button>
            <Button type="submit" disabled={busy} data-testid="admin-muni-save-btn">{busy ? "…" : t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BudgetDialog({ muni, onClose, onSaved, t }) {
  const [value, setValue] = useState(muni.monthly_credit_budget);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/municipalities/${muni.id}/budget`, {
        monthly_credit_budget: parseInt(value, 10),
      });
      toast.success(t("budget_saved"));
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-budget-dialog">
        <DialogHeader>
          <DialogTitle>
            {t("set_budget")} — {muni.name}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-3" onSubmit={submit}>
          <div>
            <Label>{t("monthly_credit_budget")}</Label>
            <Input
              type="number"
              min="0"
              step="10"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid="admin-budget-input"
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>{t("cancel")}</Button>
            <Button type="submit" disabled={busy} data-testid="admin-budget-save-btn">{busy ? "…" : t("save")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PlatformSettingsPanel({ initial, onSaved, t }) {
  const [form, setForm] = useState({
    weather_api_provider: initial.weather_api_provider || "",
    weather_api_endpoint: initial.weather_api_endpoint || "",
    weather_api_key: "",
    credit_alert_threshold: initial.credit_alert_threshold ?? 5000,
    default_monthly_budget: initial.default_monthly_budget ?? 1000,
    credit_euro_rate: initial.credit_euro_rate ?? 0.1,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, credit_euro_rate: parseFloat(form.credit_euro_rate) };
      if (!payload.weather_api_key) delete payload.weather_api_key;
      await api.patch("/admin/settings", payload);
      toast.success(t("settings_saved"));
      onSaved?.();
    } finally {
      setBusy(false);
    }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <Card className="border-border">
      <CardContent className="p-6">
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("weather_api_provider")}</Label>
              <Input value={form.weather_api_provider} onChange={(e) => set("weather_api_provider", e.target.value)} placeholder="openweathermap" data-testid="settings-weather-provider" />
            </div>
            <div>
              <Label>{t("weather_api_endpoint")}</Label>
              <Input value={form.weather_api_endpoint} onChange={(e) => set("weather_api_endpoint", e.target.value)} placeholder="https://api.openweathermap.org/data/2.5/onecall" />
            </div>
          </div>
          <div>
            <Label>
              {t("weather_api_key")}
              {initial.weather_api_key_set && (
                <span className="ml-2 text-xs text-muted-foreground">({t("key_set_hint")})</span>
              )}
            </Label>
            <Input type="password" value={form.weather_api_key} onChange={(e) => set("weather_api_key", e.target.value)} placeholder={initial.weather_api_key_set ? "•••••" : ""} data-testid="settings-weather-key" />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div>
              <Label>{t("credit_alert_threshold")}</Label>
              <Input type="number" min="0" step="100" value={form.credit_alert_threshold} onChange={(e) => set("credit_alert_threshold", parseInt(e.target.value, 10))} data-testid="settings-alert-threshold" />
            </div>
            <div>
              <Label>{t("default_monthly_budget")}</Label>
              <Input type="number" min="0" step="100" value={form.default_monthly_budget} onChange={(e) => set("default_monthly_budget", parseInt(e.target.value, 10))} data-testid="settings-default-budget" />
            </div>
            <div>
              <Label>{t("credit_euro_rate")}</Label>
              <Input type="number" min="0" step="0.01" value={form.credit_euro_rate} onChange={(e) => set("credit_euro_rate", e.target.value)} data-testid="settings-euro-rate" />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy} className="gap-2" data-testid="settings-save-btn">
              <Settings2 className="h-4 w-4" />
              {busy ? "…" : t("save")}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function BudgetWidget() {
  const { t } = useI18n();
  const [b, setB] = useState(null);
  useEffect(() => {
    api.get("/municipalities/my-budget").then((r) => setB(r.data)).catch(() => setB(null));
  }, []);
  if (!b) return null;
  const heat = b.pct >= 90 ? "border-destructive/60 text-destructive bg-destructive/5"
    : b.pct >= 60 ? "border-yellow-500/60 text-yellow-700 bg-yellow-500/5"
    : "border-primary/40 text-primary bg-primary/[0.04]";
  return (
    <Card className={`${heat} border`} data-testid="muni-budget-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Coins className="h-4 w-4" />
            <div className="label-caps">{t("credit_budget")}</div>
          </div>
          {b.topup_hint && (
            <span className="rounded-md bg-destructive px-2 py-0.5 text-xs font-semibold text-destructive-foreground" data-testid="muni-budget-topup">
              {t("topup_needed")}
            </span>
          )}
        </div>
        <div className="mt-2 flex items-baseline justify-between">
          <div className="font-mono-jb text-2xl font-semibold" data-testid="muni-budget-used">
            {b.used}
            <span className="text-sm text-muted-foreground"> / {b.monthly_budget} Cr</span>
          </div>
          <div className="font-mono-jb text-sm">{b.pct}%</div>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={b.pct >= 90 ? "h-full bg-destructive" : b.pct >= 60 ? "h-full bg-yellow-500" : "h-full bg-primary"}
            style={{ width: `${Math.min(b.pct, 100)}%` }}
            data-testid="muni-budget-bar"
          />
        </div>
      </CardContent>
    </Card>
  );
}
